// =========================
// MessagesPage.jsx (FULL FILE)
// =========================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { jwtDecode } from "jwt-decode";
import "./MessagesPage.css";

/* ---------------------------
   i18n (same logic as LandingPage.js / Wizard.js)
   - Language is controlled by SettingsPage.js via localStorage("weavevo_lang")
   - Default: EN
--------------------------- */
const MSG_LANG_KEY = "weavevo_lang";

const MSG_I18N = {
  en: {
    msg_new: "New",
    msg_share_friends: "Share with friends",
    msg_loading: "Loading…",
    msg_no_messages_yet: "No messages yet",
    msg_participants_count: "{count} participants",
    msg_you: "You",
    msg_edited: "(edited)",
    msg_reactions: "Reactions",
    msg_react_like: "Like",
    msg_react_love: "Love",
    msg_react_haha: "Haha",
    msg_react_wow: "Wow",
    msg_edit: "Edit",
    msg_delete: "Delete",
    msg_click_remove_reaction: "Click to remove reaction",
    msg_remove_reaction: "Remove reaction",
    msg_add_emoji: "Add emoji",
    msg_attach_file: "Attach file",
    msg_upload_failed: "File upload failed. Please try again.",
    msg_uploading: "Uploading…",
    msg_write_placeholder: "Write a message…",
    msg_onboard_title: "Discover. Create. Collaborate.",
    msg_onboard_welcome_before: "Welcome to ",
    msg_onboard_welcome_after: " — your creative workspace for visual storytelling powered by AI.",
    msg_onboard_step1_title: "Add your media",
    msg_onboard_step1_text: "Upload photos, clips, or voice notes — all organized automatically.",
    msg_onboard_step2_title: "Discover open projects",
    msg_onboard_step2_text: "Explore inspiring ideas and join creative journeys.",
    msg_onboard_step3_title: "Contribute and publish",
    msg_onboard_step3_text: "Collaborate with your team — your input enriches every story.",
    msg_onboard_step4_title: "Generate AI video stories",
    msg_onboard_step4_text: "Turn your media into cinematic videos with voice, motion, and sound.",
    msg_invite_title: "Invite collaborators to start creating:",
    msg_copy_invite: "Copy invite link",
    msg_email: "Email",
    msg_invite_note: "Once your collaborators join, you’ll see your messages and media appear here.",
    msg_modal_title: "New message",
    msg_search_ph: "Search people (ex: Emma)…",
    msg_remove: "Remove",
    msg_start_chat: "Start chat",
    msg_image_alt: "image",
  },
  fr: {
    msg_new: "Nouveau",
    msg_share_friends: "Partagez avec vos amis",
    msg_loading: "Chargement…",
    msg_no_messages_yet: "Aucun message pour l’instant",
    msg_participants_count: "{count} participants",
    msg_you: "Vous",
    msg_edited: "(modifié)",
    msg_reactions: "Réactions",
    msg_react_like: "J’aime",
    msg_react_love: "J’adore",
    msg_react_haha: "Haha",
    msg_react_wow: "Wow",
    msg_edit: "Modifier",
    msg_delete: "Supprimer",
    msg_click_remove_reaction: "Cliquez pour retirer la réaction",
    msg_remove_reaction: "Retirer la réaction",
    msg_add_emoji: "Ajouter un emoji",
    msg_attach_file: "Joindre un fichier",
    msg_upload_failed: "Échec de l’envoi du fichier. Réessayez.",
    msg_uploading: "Envoi…",
    msg_write_placeholder: "Écrire un message…",
    msg_onboard_title: "Découvrir. Créer. Collaborer.",
    msg_onboard_welcome_before: "Bienvenue sur ",
    msg_onboard_welcome_after: " — votre espace créatif de storytelling visuel, propulsé par l’IA.",
    msg_onboard_step1_title: "Ajoutez vos médias",
    msg_onboard_step1_text: "Importez photos, clips ou notes vocales — tout est organisé automatiquement.",
    msg_onboard_step2_title: "Découvrez des projets ouverts",
    msg_onboard_step2_text: "Explorez des idées inspirantes et rejoignez des journeys créatifs.",
    msg_onboard_step3_title: "Contribuez et publiez",
    msg_onboard_step3_text: "Collaborez avec votre équipe — chaque contribution enrichit l’histoire.",
    msg_onboard_step4_title: "Générez des vidéos IA",
    msg_onboard_step4_text: "Transformez vos médias en vidéos cinématiques avec voix, mouvement et son.",
    msg_invite_title: "Invitez des collaborateurs pour commencer :",
    msg_copy_invite: "Copier le lien d’invitation",
    msg_email: "E-mail",
    msg_invite_note: "Une fois vos collaborateurs inscrits, vos messages et médias apparaîtront ici.",
    msg_modal_title: "Nouveau message",
    msg_search_ph: "Rechercher des personnes (ex : Emma)…",
    msg_remove: "Retirer",
    msg_start_chat: "Démarrer le chat",
    msg_image_alt: "image",
  },
};

/* ---------------------------
   Helpers
--------------------------- */
function letterAvatar(name, size = 40, extra = "") {
  const initial = (name || "?").toString().trim().charAt(0).toUpperCase();
  return (
    <div
      className={`rounded-circle bg-dark text-white d-flex align-items-center justify-content-center shadow-sm ${extra}`}
      style={{ width: size, height: size, fontWeight: 700 }}
      title={name}
    >
      {initial}
    </div>
  );
}

const REACTION_CHOICES = [
  { key: "like", emoji: "👍", labelKey: "msg_react_like" },
  { key: "heart", emoji: "❤️", labelKey: "msg_react_love" },
  { key: "laugh", emoji: "😂", labelKey: "msg_react_haha" },
  { key: "wow", emoji: "😮", labelKey: "msg_react_wow" },
];

function reactionToEmoji(key) {
  return (
    REACTION_CHOICES.find((r) => r.key === key)?.emoji ||
    (key === "like"
      ? "👍"
      : key === "heart"
      ? "❤️"
      : key === "laugh"
      ? "😂"
      : key === "wow"
      ? "😮"
      : "")
  );
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "heic"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "mkv", "avi"];

function getFileExtension(value) {
  if (!value) return "";
  const str = String(value).split("?")[0]; // drop query params
  const parts = str.split(".");
  if (parts.length < 2) return "";
  return parts.pop().toLowerCase();
}

function isImageAttachment(msg = {}) {
  const mime = msg.file_mime || msg.mime_type;
  if (typeof mime === "string" && mime.toLowerCase().startsWith("image/")) {
    return true;
  }
  const ext = getFileExtension(msg.file_name || msg.file_url || msg.body || "");
  return IMAGE_EXTENSIONS.includes(ext);
}

function isVideoAttachment(msg = {}) {
  const mime = msg.file_mime || msg.mime_type;
  if (typeof mime === "string" && mime.toLowerCase().startsWith("video/")) {
    return true;
  }
  const ext = getFileExtension(msg.file_name || msg.file_url || msg.body || "");
  return VIDEO_EXTENSIONS.includes(ext);
}

function Avatar({ src, name, size = 40, className = "" }) {
  if (!src) return letterAvatar(name, size, className);
  return (
    <img
      src={src}
      alt={name}
      width={size}
      height={size}
      className={`rounded-circle shadow-sm ${className}`}
      style={{ objectFit: "cover" }}
      onError={(e) => e.currentTarget.replaceWith(letterAvatar(name, size))}
    />
  );
}

/** Stacked thumbnails for group chats (up to 3) */
function StackedThumbs({ users, size = 34 }) {
  const shown = (users || []).slice(0, 3);
  return (
    <div className="position-relative" style={{ width: size + 16, height: size }}>
      {shown.map((u, i) => (
        <div
          key={u.username || i}
          className="position-absolute"
          style={{ left: i * (size * 0.45), top: 0, zIndex: 3 - i }}
        >
          <Avatar src={u.avatar_url} name={u.display_name || u.username} size={size} />
        </div>
      ))}
    </div>
  );
}

function findUserMeta(users, username) {
  const u = (users || []).find(
    (x) => x.username?.toLowerCase() === String(username || "").toLowerCase()
  );
  return {
    username,
    display_name: u?.display_name || u?.firstName || u?.lastName || username,
    avatar_url: u?.avatar_url || null,
  };
}

/* ---------------------------
   Messaging Page
--------------------------- */
export default function MessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useAuth();
  const token = auth?.token;
  const decoded = token ? jwtDecode(token) : {};
  const me = decoded?.preferred_username || "";
  const user = auth.user;

  const kc_token = token || localStorage.getItem("kc_token") || "";
  const [params, setParams] = useSearchParams();
  const activeThreadId = params.get("thread");
  const toUser = params.get("to"); // deep-link from profile
  const [uploading, setUploading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // i18n
  const [lang, setLang] = useState(() => {
    try {
      const raw =
        localStorage.getItem(MSG_LANG_KEY) ||
        localStorage.getItem("weavevo_language") ||
        "en";
      const norm = String(raw || "").toLowerCase().trim().slice(0, 2);
      return norm === "fr" ? "fr" : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    const readLang = () => {
      try {
        const raw =
          localStorage.getItem(MSG_LANG_KEY) ||
          localStorage.getItem("weavevo_language") ||
          "en";
        const norm = String(raw || "").toLowerCase().trim().slice(0, 2);
        const next = norm === "fr" ? "fr" : "en";
        setLang(next);
        document.documentElement.lang = next;
      } catch {
        // ignore
      }
    };

    readLang();
    window.addEventListener("storage", readLang);
    window.addEventListener("focus", readLang);
    document.addEventListener("visibilitychange", readLang);
    return () => {
      window.removeEventListener("storage", readLang);
      window.removeEventListener("focus", readLang);
      document.removeEventListener("visibilitychange", readLang);
    };
  }, []);

  const t = useCallback(
    (key, vars = {}) => {
      const dict = MSG_I18N[lang] || MSG_I18N.en;
      const fallback = MSG_I18N.en;
      let s = dict[key] ?? fallback[key] ?? key;
      Object.entries(vars || {}).forEach(([k, v]) => {
        s = s.replaceAll(`{${k}}`, String(v));
      });
      return s;
    },
    [lang]
  );

  const locale = lang === "fr" ? "fr-FR" : "en-US";

  // New message state
  const [newOpen, setNewOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]); // array of usernames

  // Messages in the active thread
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);
  const deepLinkOnceRef = useRef(false);
  const [hovered, setHovered] = useState(null);
  const [editMessage, setEditMessage] = useState(null);

  async function deleteMessage(id) {
    const res = await fetch(`/messaging/messages/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${kc_token}` },
    });
    if (res.ok) setMessages((prev) => prev.filter((m) => m.id !== id));
  }

  async function handleEditSave() {
    if (!editMessage) return;

    try {
      // normalize possible "file-123" ids
      const realId = String(editMessage.id).replace(/^file-/, "");

      const res = await fetch(`/messaging/messages/${realId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${kc_token}`,
        },
        body: JSON.stringify({ body: editMessage.body }),
      });

      if (!res.ok) {
        const t_ = await res.text().catch(() => "");
        throw new Error(`Update failed: ${res.status} ${t_}`);
      }
      const updated = await res.json();
      const uid = Number(updated.id); // <- make sure we compare numbers

      setMessages((prev) =>
        prev.map((m) =>
          Number(m.id) === uid
            ? { ...m, body: updated.body, updated_at: new Date().toISOString() }
            : m
        )
      );

      // also refresh the thread list preview (last message text)
      loadThreads?.();

      setEditMessage(null);
    } catch (err) {
      console.error("❌ Edit save error:", err);
    }
  }

  async function toggleReaction(messageId, emoji) {
    if (!kc_token) {
      console.error("❌ No token: cannot react");
      return;
    }
    try {
      const res = await fetch(`/messaging/messages/${messageId}/react`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${kc_token}`,
        },
        body: JSON.stringify({ reaction: emoji }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error(`❌ /react failed: ${res.status} ${res.statusText}`, text);
        throw new Error(`Reaction failed (${res.status})`);
      }

      const data = await res.json(); // { message_id, reaction: "heart" | "" }
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, reactions: data.reaction } : m))
      );
    } catch (err) {
      console.error("❌ toggleReaction error:", err);
    }
  }

  useEffect(() => {
    if (!kc_token) return;
    fetch("/users", { headers: { Authorization: `Bearer ${kc_token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAllUsers)
      .catch(() => {});
  }, [kc_token]);


// ---------------------------
// Thread identity / de-dup
// - Same sender/receiver => same DM thread
// - Same group of participants => same group thread
// ---------------------------
const normUser = (v) => String(v || "").trim().toLowerCase();
const uniqSorted = (arr) => {
  const set = new Set();
  (arr || []).forEach((x) => {
    const n = normUser(x);
    if (n) set.add(n);
  });
  return Array.from(set).sort();
};

const buildThreadKey = (fullNormParticipants) => {
  const p = uniqSorted(fullNormParticipants);
  if (p.length <= 2) return `dm:${p.join(":")}`;
  return `grp:${p.join(":")}`;
};

const getThreadNormParticipants = (t0) => {
  const raw =
    Array.isArray(t0?.participants) && t0.participants.length
      ? t0.participants
      : [t0?.sender, t0?.receiver].filter(Boolean);
  return uniqSorted(raw);
};

const getThreadHasMsg = (t0) => {
  if (!t0) return false;
  if (typeof t0.message_count === "number") return t0.message_count > 0;
  if (typeof t0.messages_count === "number") return t0.messages_count > 0;
  if (t0.last_body) return true;
  const lm = t0.last_message || t0.lastMessage;
  if (lm && (lm.text || lm.body || lm.message)) return true;
  return false;
};

const parseDateMs = (v) => {
  if (!v) return 0;
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const getThreadLastMs = (t0) => {
  // prefer last activity; fallback to created
  const ms =
    parseDateMs(t0?.last_message_at) ||
    parseDateMs(t0?.updated_at) ||
    parseDateMs(t0?.last_message?.created_at) ||
    parseDateMs(t0?.last_message?.createdAt) ||
    parseDateMs(t0?.created_at) ||
    parseDateMs(t0?.createdAt);
  return ms || 0;
};

const pickBetterThread = (a, b) => {
  if (!a) return b;
  if (!b) return a;

  const aHas = getThreadHasMsg(a);
  const bHas = getThreadHasMsg(b);

  // 1) Prefer the one that actually has messages / last body
  if (aHas && !bHas) return a;
  if (bHas && !aHas) return b;

  // 2) Otherwise prefer most recent activity
  const ams = getThreadLastMs(a);
  const bms = getThreadLastMs(b);
  if (ams > bms) return a;
  if (bms > ams) return b;

  // 3) Stable tie-breaker: larger id wins (often newer)
  const aid = Number(a?.id) || 0;
  const bid = Number(b?.id) || 0;
  return bid > aid ? b : a;
};

const dedupeThreadsByParticipants = (threadsList) => {
  const map = new Map(); // key -> best thread
  for (const th of threadsList || []) {
    const participants = getThreadNormParticipants(th);
    const key = buildThreadKey(participants);
    const prev = map.get(key);
    map.set(key, pickBetterThread(prev, th));
  }
  // sort by last activity desc (then hasMsg)
  return Array.from(map.values()).sort((a, b) => {
    const aHas = getThreadHasMsg(a);
    const bHas = getThreadHasMsg(b);
    if (aHas !== bHas) return aHas ? -1 : 1;
    return getThreadLastMs(b) - getThreadLastMs(a);
  });
};

const findBestThreadByParticipants = (threadsList, fullParticipants) => {
  const wantKey = buildThreadKey(uniqSorted(fullParticipants));
  let best = null;
  for (const th of threadsList || []) {
    const key = buildThreadKey(getThreadNormParticipants(th));
    if (key !== wantKey) continue;
    best = pickBetterThread(best, th);
  }
  return best;
};

const loadThreads = useCallback(async () => {
  if (!kc_token) return [];
  setLoading(true);
  try {
    const r = await fetch("/messaging/threads", {
      headers: { Authorization: `Bearer ${kc_token}` },
    });
    const arr = r.ok ? await r.json() : [];
    setThreads(arr || []);
    return arr || [];
  } catch {
    return [];
  } finally {
    setLoading(false);
  }
}, [kc_token]);

useEffect(() => {
  loadThreads();
}, [loadThreads]);

// UI list + resolver should use a deduped view (same participants => one thread)
const dedupedThreads = useMemo(() => dedupeThreadsByParticipants(threads), [threads]);


// Resolve thread for a given set of "other participants" (usernames excluding me).
// 1) Try existing thread in local list
// 2) Refresh from backend and retry
// 3) Create/resolve on backend (idempotent recommended)
const resolveThreadForParticipants = useCallback(
  async (othersRaw) => {
    const others = (othersRaw || [])
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .filter((x) => normUser(x) !== normUser(me)); // safety: never include me in "others"

    const fullList = [me, ...others].filter(Boolean);

    // 1) Local match
    const localMatch = findBestThreadByParticipants(dedupedThreads, fullList);
    if (localMatch?.id) return localMatch;

    // 2) Fresh match
    const fresh = await loadThreads();
    const freshDeduped = dedupeThreadsByParticipants(fresh);
    const freshMatch = findBestThreadByParticipants(freshDeduped, fullList);
    if (freshMatch?.id) return freshMatch;

    // 3) Backend resolve/create
    const payload = {
      // Backward-compatible: your backend currently expects "participants" = others
      participants: others,
      // Optional fields that help dedupe on backend (recommended)
      thread_key: buildThreadKey(fullList),
      participants_all: uniqSorted(fullList),
    };

    // Prefer a dedicated resolve endpoint if you add it server-side
    let res = await fetch(`/messaging/threads/resolve`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${kc_token}`,
      },
      body: JSON.stringify(payload),
    });

    // Fallback to existing endpoint
    if (res.status === 404) {
      res = await fetch(`/messaging/threads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${kc_token}`,
        },
        body: JSON.stringify(payload),
      });
    }

    if (!res.ok) return null;
    const t_ = await res.json();
    loadThreads();
    return t_;
  },
  [kc_token, me, dedupedThreads, loadThreads]
);

  // Deep-link: /messages?to=<username> (open DM with this user)
  // IMPORTANT: we RESOLVE thread by participants (no duplicates)
  useEffect(() => {
    if (deepLinkOnceRef.current) return;

    const target = (toUser || "").trim();
    if (!kc_token) return;
    if (!target) return;

    // If thread already selected, don't override
    if (activeThreadId) {
      setParams(
        (prev) => {
          prev.delete("to");
          return prev;
        },
        { replace: true }
      );
      return;
    }

    // Avoid self-DM
    if (normUser(target) === normUser(me)) {
      setParams(
        (prev) => {
          prev.delete("to");
          return prev;
        },
        { replace: true }
      );
      return;
    }

    deepLinkOnceRef.current = true;

    (async () => {
      try {
        const t_ = await resolveThreadForParticipants([target]);
        if (!t_?.id) {
          deepLinkOnceRef.current = false; // allow retry
          return;
        }

        setParams(
          (prev) => {
            prev.set("thread", String(t_.id));
            prev.delete("to");
            return prev;
          },
          { replace: true }
        );
      } catch (err) {
        console.error("❌ Deep-link DM error:", err);
        deepLinkOnceRef.current = false;
      }
    })();
  }, [kc_token, toUser, activeThreadId, me, resolveThreadForParticipants]);

const normalizeReaction = (r) => {
    if (!r) return "";
    if (typeof r === "string") return r;
    if (typeof r === "object") {
      const first = Object.keys(r).find((k) => r[k] > 0);
      return first || "";
    }
    return "";
  };

  useEffect(() => {
    if (!kc_token || !activeThreadId) {
      setMessages([]);
      return;
    }

    fetch(`/messaging/threads/${activeThreadId}/messages`, {
      headers: { Authorization: `Bearer ${kc_token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((arr) =>
        setMessages(
          (arr || []).map((m) => ({
            ...m,
            reactions: normalizeReaction(m.reactions),
          }))
        )
      )
      .catch(() => {});

    fetch(`/messaging/threads/${activeThreadId}/read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${kc_token}` },
    }).finally(() => {
      fetch(`/messaging/unread-count`, {
        headers: { Authorization: `Bearer ${kc_token}` },
      })
        .then((r) => (r.ok ? r.json() : { count: 0 }))
        .then((d) =>
          window.dispatchEvent(
            new CustomEvent("messaging:unread", {
              detail: { count: d.count || 0 },
            })
          )
        )
        .catch(() => {});
    });
  }, [kc_token, activeThreadId]);

  /* Autoscroll */
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* Derived map for quick lookup */
  const usersByName = useMemo(() => {
    const m = new Map();
    (allUsers || []).forEach((u) => m.set(u.username, u));
    return m;
  }, [allUsers]);

  function openThread(id) {
    setParams(
      (prev) => {
        prev.set("thread", String(id));
        prev.delete("to");
        return prev;
      },
      { replace: true }
    );
  }

/* Sending a message */
  async function send() {
    const body = draft.trim();
    if (!body || !activeThreadId) return;

    const optimistic = {
      id: `tmp-${Date.now()}`,
      sender: me,
      body,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    endRef.current?.scrollIntoView({ behavior: "smooth" });

    const res = await fetch(`/messaging/threads/${activeThreadId}/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${kc_token || ""}`,
      },
      body: JSON.stringify({ body }),
    });

    if (res.ok) {
      const saved = await res.json();
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? saved : m)));
      loadThreads();
    } else {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      console.error("Send failed:", res.status);
    }
  }

  /* Create a DM or group from the composer */
  async function createThreadFromSelection() {
    if (!selected.length) return;

    const t_ = await resolveThreadForParticipants(selected);
    if (!t_?.id) return;

    setNewOpen(false);
    setSelected([]);
    setQuery("");
    openThread(t_.id);
  }

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return (allUsers || []).filter((u) => u.username !== me);
    return (allUsers || [])
      .filter(
        (u) =>
          (u.username || "").toLowerCase().includes(q) ||
          (u.display_name || "").toLowerCase().includes(q) ||
          (u.handle || "").toLowerCase().includes(q)
      )
      .filter((u) => u.username !== me);
  }, [allUsers, query, me]);

  /* UI */
  return (
    <div
      className="container-fluid messages-page"
      style={{ paddingTop: 74, background: "#f4f5f8", minHeight: "100vh" }}
    >
      <div className="row">
        {/* LEFT: Threads list */}
        <div
          className="col-12 col-md-4 col-lg-3 border-end bg-white"
          style={{ height: "calc(100vh - 90px)", overflowY: "auto" }}
        >
          <div className="p-2 d-flex justify-content-start align-items-center gap-2 border-bottom bg-white wv-thread-toolbar flex-nowrap">
            {/* New */}
            <button
              className="btn btn-dark btn-sm rounded-pill d-flex align-items-center gap-2 px-3 py-1 shadow-sm"
              onClick={() => setNewOpen(true)}
              style={{
                fontWeight: 500,
                height: "30px",
                lineHeight: "1",
              }}
            >
              <i className="bi bi-pencil-square"></i>
              <span>{t("msg_new")}</span>
            </button>

            {/* Share */}
            <button
              className="btn btn-outline-dark btn-sm rounded-pill d-flex align-items-center gap-2 px-3 py-1 shadow-sm wv-share-btn"
              onClick={() => navigate("/messages")}
              style={{
                fontWeight: 500,
                height: "30px",
                lineHeight: "1",
              }}
            >
              <i className="bi bi-person-plus"></i>
              <span className="wv-btn-label">{t("msg_share_friends")}</span>
            </button>
          </div>

          {loading && <div className="p-3 text-muted">{t("msg_loading")}</div>}

          <ul className="list-group list-group-flush">
            {dedupedThreads.map((t0) => {
              const isDM = t0.type === "dm";
              let title = t0.title || "";
              let thumb = null;

              if (isDM) {
                const other =
                  (t0.participants || []).find((p) => p && p !== me) || t0.receiver || t0.sender;
                const meta = findUserMeta(allUsers, other);
                title = meta.display_name || other;
                thumb = <Avatar src={meta.avatar_url} name={title} className="me-2" />;
              } else {
                const metas = (t0.participants || [])
                  .filter((p) => p !== me)
                  .map((p) => findUserMeta(allUsers, p));
                thumb = <StackedThumbs users={metas} />;
                title = t0.title || t("msg_participants_count", { count: (t0.participants || []).length });
              }

              return (
                <li
                  key={t0.id}
                  className={`list-group-item list-group-item-action d-flex gap-2 align-items-center ${
                    String(activeThreadId) === String(t0.id) ? "active" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => openThread(t0.id)}
                >
                  <div style={{ width: 46 }}>{thumb}</div>
                  <div
                    className="flex-grow-1"
                    style={{
                      marginLeft: "20px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <div className="fw-semibold text-truncate">{title}</div>
                    <div className="text-muted small text-truncate">
                      {t0.last_body || t0.last_message?.text || t("msg_no_messages_yet")}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div
          className="col-12 col-md-8 col-lg-9 d-flex flex-column"
          style={{
            height: "calc(100vh - 90px)",
            background: "#f4f5f8",
            overflow: "hidden",
          }}
        >
          {activeThreadId ? (
            // Conversation
            <div className="d-flex flex-column h-100 px-3 py-4">
              {/* Scrollable conversation card */}
              <div
                className="flex-grow-1 mb-3 rounded-4 bg-white shadow-sm p-3"
                style={{
                  overflowY: "auto",
                  minHeight: 0, // important for flex + overflow
                }}
              >
                {messages.map((m) => {
                  const mine = String(m.sender) === String(me);
                  const meta = findUserMeta(allUsers, m.sender);

                  return (
                    <div
                      key={m.id}
                      className={`mb-3 d-flex ${mine ? "justify-content-end" : ""}`}
                      onMouseEnter={() => setHovered(m.id)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {!mine && (
                        <a
                          className="me-2"
                          href={`/profile/${meta.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Avatar src={meta.avatar_url} name={meta.display_name} />
                        </a>
                      )}

                      <div className="position-relative" style={{ maxWidth: "72%" }}>
                        <div className="small text-muted fw-semibold mb-1">
                          {mine ? t("msg_you") : meta.display_name} ·{" "}
                          {m.created_at ? new Date(m.created_at).toLocaleString(locale) : ""}
                        </div>

                        {/* === Message Bubble Wrapper === */}
                        <div
                          className="message-bubble-wrapper"
                          onMouseEnter={() => setHovered(m.id)}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <div className={`message-bubble ${mine ? "mine" : "theirs"}`}>
                            {m.file_url ? (
                              isImageAttachment(m) ? (
                                <div>
                                  <a href={m.file_url} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={m.file_url}
                                      alt={m.file_name || t("msg_image_alt")}
                                      style={{
                                        maxWidth: "260px",
                                        maxHeight: "180px",
                                        borderRadius: 12,
                                        display: "block",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </a>
                                  {(m.file_name || m.body) && (
                                    <div
                                      className="small text-muted mt-2 d-flex align-items-center"
                                      style={{ gap: 6 }}
                                    >
                                      <i className="bi bi-paperclip" />
                                      <span className="text-truncate">{m.file_name || m.body}</span>
                                    </div>
                                  )}
                                </div>
                              ) : isVideoAttachment(m) ? (
                                <div>
                                  <video
                                    src={m.file_url}
                                    controls
                                    style={{
                                      maxWidth: "260px",
                                      maxHeight: "180px",
                                      borderRadius: 12,
                                      display: "block",
                                      objectFit: "cover",
                                    }}
                                  />
                                  {(m.file_name || m.body) && (
                                    <div
                                      className="small text-muted mt-2 d-flex align-items-center"
                                      style={{ gap: 6 }}
                                    >
                                      <i className="bi bi-paperclip" />
                                      <span className="text-truncate">{m.file_name || m.body}</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <a
                                  href={m.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: mine ? "#fff" : "#0d6efd",
                                    textDecoration: "none",
                                    fontWeight: 500,
                                  }}
                                >
                                  <i className="bi bi-paperclip me-2"></i>
                                  {m.file_name || m.body}
                                </a>
                              )
                            ) : (
                              <span style={{ whiteSpace: "pre-wrap" }}>{m.body || m.text}</span>
                            )}

                            {m.updated_at && (
                              <small className="text-muted ms-2 fst-italic">{t("msg_edited")}</small>
                            )}
                          </div>

                          {/* Reaction + actions bar */}
                          {hovered === m.id && (
                            <div className="reaction-bar" role="group" aria-label={t("msg_reactions")}>
                              {REACTION_CHOICES.map((r) => (
                                <button
                                  key={r.key}
                                  type="button"
                                  className={`reaction-btn ${m.reactions === r.key ? "active" : ""}`}
                                  title={t(r.labelKey)}
                                  aria-label={t(r.labelKey)}
                                  onClick={() => toggleReaction(m.id, r.key)}
                                >
                                  <span className="reaction-emoji" aria-hidden="true">
                                    {r.emoji}
                                  </span>
                                </button>
                              ))}

                              {mine && (
                                <>
                                  <span className="reaction-sep" aria-hidden="true" />

                                  <button
                                    type="button"
                                    className="reaction-icon-btn"
                                    title={t("msg_edit")}
                                    aria-label={t("msg_edit")}
                                    onClick={() => setEditMessage(m)}
                                  >
                                    <i className="bi bi-pencil-square" />
                                  </button>

                                  <button
                                    type="button"
                                    className="reaction-icon-btn danger"
                                    title={t("msg_delete")}
                                    aria-label={t("msg_delete")}
                                    onClick={() => deleteMessage(m.id)}
                                  >
                                    <i className="bi bi-trash3-fill" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}

                          {/* Existing reaction chip */}
                          {m.reactions && m.reactions !== "" && (
                            <button
                              type="button"
                              className="existing-reactions"
                              title={t("msg_click_remove_reaction")}
                              aria-label={t("msg_remove_reaction")}
                              onClick={() => toggleReaction(m.id, m.reactions)}
                            >
                              <span className="reaction-single" aria-hidden="true">
                                {reactionToEmoji(m.reactions)}
                              </span>
                              <span className="reaction-count" aria-hidden="true">
                                1
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div ref={endRef} />
              </div>

              {/* Composer docked at bottom */}
              <div className="rounded-4 bg-white shadow-sm px-3 py-2 d-flex align-items-center gap-2">
                {/* Emoji Picker Dropdown */}
                <div className="dropdown me-2">
                  <button
                    className="btn btn-light rounded-circle border-0 d-flex align-items-center justify-content-center"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title={t("msg_add_emoji")}
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-emoji-smile fs-5 text-secondary"></i>
                  </button>

                  <ul
                    className="dropdown-menu p-2 shadow-sm border-0"
                    style={{
                      minWidth: 250,
                      maxHeight: 180,
                      overflowY: "auto",
                      fontSize: "1.25rem",
                    }}
                  >
                    {[
                      "😀",
                      "😁",
                      "😂",
                      "🤣",
                      "😊",
                      "😍",
                      "😎",
                      "👍",
                      "🙏",
                      "🔥",
                      "🎉",
                      "❤️",
                      "💪",
                      "😢",
                      "😮",
                    ].map((emoji) => (
                      <li
                        key={emoji}
                        role="button"
                        className="d-inline-block p-1 rounded"
                        onClick={() => setDraft((prev) => (prev || "") + emoji)}
                      >
                        {emoji}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* File upload with loader */}
                <div className="position-relative me-2">
                  <label className="btn btn-light border-0 p-0" title={t("msg_attach_file")}>
                    <i className="bi bi-paperclip fs-5 text-secondary"></i>
                    <input
                      type="file"
                      className="d-none"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file || !activeThreadId) return;

                        setUploading(true);
                        try {
                          const formData = new FormData();
                          formData.append("file", file);

                          const uploadRes = await fetch(
                            `/messaging/threads/${activeThreadId}/files`,
                            {
                              method: "POST",
                              headers: { Authorization: `Bearer ${kc_token}` },
                              body: formData,
                            }
                          );

                          if (!uploadRes.ok) throw new Error("Upload failed");

                          const saved = await uploadRes.json();
                          setMessages((prev) => [
                            ...prev,
                            {
                              id: `file-${Date.now()}`,
                              sender: me,
                              body: `📎 ${file.name}`,
                              file_url: saved.url,
                              file_name: file.name,
                              file_mime: file.type,
                              created_at: new Date().toISOString(),
                            },
                          ]);

                          loadThreads();
                        } catch (err) {
                          console.error("❌ Upload error:", err);
                          alert(t("msg_upload_failed"));
                        } finally {
                          setUploading(false);
                          e.target.value = "";
                        }
                      }}
                    />
                  </label>

                  {uploading && (
                    <div
                      className="position-absolute top-0 start-100 translate-middle-y ms-1"
                      style={{ zIndex: 5 }}
                    >
                      <div className="spinner-border spinner-border-sm text-secondary" role="status">
                        <span className="visually-hidden">{t("msg_uploading")}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Text Input */}
                <input
                  type="text"
                  className="form-control border-0 shadow-none flex-grow-1"
                  placeholder={t("msg_write_placeholder")}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  style={{
                    outline: "none",
                    background: "transparent",
                    fontSize: "1rem",
                  }}
                />

                {/* Send Button */}
                <button
                  className="btn btn-dark rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 42, height: 42 }}
                  onClick={send}
                >
                  <i className="bi bi-send-fill fs-5"></i>
                </button>
              </div>
            </div>
          ) : (
            // Onboarding panel
            <div
              className="d-flex flex-column align-items-start justify-content-start text-center h-100 px-3 py-4"
              style={{ overflowY: "auto" }}
            >
              <div
                className="onboarding-card shadow-lg bg-white rounded-4 p-4 mt-4 mb-5"
                style={{
                  maxWidth: "820px",
                  width: "100%",
                  border: "1px solid #eef1f5",
                  background: "linear-gradient(135deg, #fafbff 0%, #f3f7ff 100%)",
                }}
              >
                <i className="bi bi-stars text-primary fs-1 mb-3" style={{ opacity: 0.9 }}></i>
                <h3 className="fw-bold text-dark mb-2">{t("msg_onboard_title")}</h3>
                <p className="text-muted mb-4" style={{ fontSize: "1rem" }}>
                  {t("msg_onboard_welcome_before")}
                  <strong>Weavevo</strong>
                  {t("msg_onboard_welcome_after")}
                </p>

                <div
                  className="d-flex flex-column align-items-start gap-4 text-start mx-auto"
                  style={{ maxWidth: "580px" }}
                >
                  {[
                    {
                      color: "success",
                      icon: "bi-upload",
                      title: t("msg_onboard_step1_title"),
                      text: t("msg_onboard_step1_text"),
                    },
                    {
                      color: "warning",
                      icon: "bi-binoculars",
                      title: t("msg_onboard_step2_title"),
                      text: t("msg_onboard_step2_text"),
                    },
                    {
                      color: "danger",
                      icon: "bi-people",
                      title: t("msg_onboard_step3_title"),
                      text: t("msg_onboard_step3_text"),
                    },
                    {
                      color: "dark",
                      icon: "bi-magic",
                      title: t("msg_onboard_step4_title"),
                      text: t("msg_onboard_step4_text"),
                    },
                  ].map((step, i) => (
                    <div key={i} className="d-flex align-items-start gap-3">
                      <div
                        className={`rounded-circle bg-${step.color} d-flex align-items-center justify-content-center text-white shadow-sm`}
                        style={{ width: 42, height: 42, flexShrink: 0 }}
                      >
                        <i className={step.icon}></i>
                      </div>
                      <div>
                        <div className="fw-semibold text-dark">{step.title}</div>
                        <div className="text-muted small">{step.text}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5">
                  <h6 className="fw-semibold mb-3 text-dark">{t("msg_invite_title")}</h6>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      className="btn btn-outline-primary rounded-pill px-3"
                      onClick={() => navigator.clipboard.writeText("https://weavevo.com/join")}
                    >
                      <i className="bi bi-link-45deg me-2"></i> {t("msg_copy_invite")}
                    </button>
                    <a
                      href="https://wa.me/?text=Join%20our%20Weavevo%20workspace%20https%3A%2F%2Fweavevo.com%2Fjoin"
                      className="btn btn-outline-success rounded-pill px-3"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="bi bi-whatsapp me-2"></i> WhatsApp
                    </a>
                    <a
                      href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fweavevo.com%2Fjoin"
                      className="btn btn-outline-primary rounded-pill px-3"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="bi bi-facebook me-2"></i> Facebook
                    </a>
                    <a
                      href="https://x.com/intent/tweet?url=https%3A%2F%2Fweavevo.com%2Fjoin"
                      className="btn btn-outline-dark rounded-pill px-3"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="bi bi-twitter-x me-2"></i> X
                    </a>
                    <a
                      href="mailto:?subject=Join our Weavevo workspace&body=Hey! Check this out: https://weavevo.com/join"
                      className="btn btn-outline-secondary rounded-pill px-3"
                    >
                      <i className="bi bi-envelope me-2"></i> {t("msg_email")}
                    </a>
                  </div>
                </div>

                <p className="text-muted small mt-4 mb-0">{t("msg_invite_note")}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Composer modal */}
      {newOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,.35)" }}
          tabIndex={-1}
          onClick={() => setNewOpen(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">{t("msg_modal_title")}</h5>
                <button type="button" className="btn-close" onClick={() => setNewOpen(false)} />
              </div>
              <div className="modal-body">
                <input
                  className="form-control mb-3"
                  placeholder={t("msg_search_ph")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div className="mb-2">
                  {selected.map((u) => {
                    const meta = usersByName.get(u);
                    return (
                      <span className="badge rounded-pill bg-dark me-2 mb-2" key={u}>
                        {meta?.display_name || u}
                        <button
                          className="btn btn-sm btn-link text-white ms-1 p-0"
                          onClick={() => setSelected((s) => s.filter((x) => x !== u))}
                          title={t("msg_remove")}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {filteredUsers.map((u) => (
                    <button
                      key={u.username}
                      className="list-group-item list-group-item-action d-flex align-items-center w-100"
                      onClick={() => setSelected((s) => (s.includes(u.username) ? s : [...s, u.username]))}
                    >
                      <Avatar src={u.avatar_url} name={u.display_name || u.username} className="me-2" />
                      <div className="text-start">
                        <div className="fw-semibold">{u.display_name || u.username}</div>
                        <div className="text-muted small">@{u.username}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-dark rounded-pill"
                  disabled={!selected.length}
                  onClick={createThreadFromSelection}
                >
                  {t("msg_start_chat")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
